'use client';

import { OptimizedImage } from '@/components/optimized-image';
import Link from 'next/link';
import { Grid2x2Plus, Heart, Eye, Play, Image as ImageIcon, PenSquare, Repeat2, Cpu, Music, Box, Pin, UserPlus } from 'lucide-react';
import { ChatIcon } from '@/components/icons/chat-icon';
import { Avatar } from '@/components/avatar';
import { formatCount, formatRelativeTime, cn } from '@/lib/utils';
import { useCallback, useState } from 'react';
import { useAuth } from '@/components/auth-provider';
import { haptic } from '@/lib/haptic';
import { CardMoreMenu } from './card-more-menu';
import { FlagReportModal } from '@/components/flag-report-modal';
import { Tip } from '@/components/ui/tooltip';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { useTranslation } from '@/components/i18n-provider';
import type { ViewMode } from './use-discover';
import { isLocalContentUrl } from '@/lib/content/url';

/**
 * Pick the right image URL for a post card. For videos that don't have a
 * `thumbnailUrl` set on the post but whose `mediaUrl` points at our
 * `/api/content/{uuid}` endpoint, fall back to the matching
 * `/api/content/{uuid}/preview` route — which lazy-generates a JPEG poster
 * server-side via FFmpeg (and caches it on disk + immutable HTTP cache).
 * Avoids shipping multi-MB video bytes for a card that never plays inline,
 * and works for legacy posts created before the upload pipeline started
 * setting `thumbnailUrl` automatically.
 */
// Extension allowlist of formats next/image can decode. Anything outside
// this set (notably video/audio) gets routed to the empty-string fallback
// so we render the gradient placeholder instead of letting the optimizer
// 400 on a non-image content type.
const IMAGE_EXTS = new Set([
  'jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'bmp', 'svg', 'ico', 'tiff',
]);

function isLikelyImageUrl(url: string): boolean {
  // No-extension URLs (e.g. `/api/content/{uuid}` without `?v=`) are
  // assumed image-shaped — the route handler returns the right mime
  // type and the optimizer is bypassed for them anyway via
  // `OptimizedImage`'s `unoptimized` path.
  const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase();
  if (!ext || ext.length > 5) return true;
  return IMAGE_EXTS.has(ext);
}

/**
 * Best IMAGE source for a post tile: explicit thumbnail, else the derived
 * video poster (`/api/content/{id}/preview`), else the mediaUrl when it is
 * image-shaped. Empty string = no usable image (caller shows a gradient or
 * a live <video> frame). Exported for every compact post tile — the
 * Discover community rows reuse it rather than re-deriving posters.
 */
export function resolveCardImageSrc(post: {
  mediaType?: string | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
}): string {
  if (post.thumbnailUrl) return post.thumbnailUrl;
  // Any NON-image media (video, 3d, audio) with a content-api mediaUrl gets
  // the derived poster — pointing an <img> at a .glb or .mp4 renders the
  // broken-image glyph. Video was already covered; 3d posts were the ones
  // showing up broken in the community column.
  if (post.mediaType && post.mediaType !== 'image' && post.mediaUrl) {
    const m = post.mediaUrl.match(/\/api\/content\/([0-9a-f-]{36})(?:\?.*)?$/i);
    if (m) return `/api/content/${m[1]}/preview`;
  }
  // Last-ditch fallback. Some legacy seed posts (curated-posts.json,
  // pre-mediaType migration) carry a `.mp4` mediaUrl with no
  // `mediaType` set — passing those into <OptimizedImage> would 400
  // through `_next/image` (non-image content type). Filter on
  // extension shape so the caller renders the gradient placeholder
  // instead.
  if (post.mediaUrl && isLikelyImageUrl(post.mediaUrl)) {
    return post.mediaUrl;
  }
  return '';
}

interface PostDiscoverCardProps {
  post: {
    id: string;
    title?: string | null;
    content?: string | null;
    mediaType?: string | null;
    mediaUrl?: string | null;
    thumbnailUrl?: string | null;
    chatCta?: string | null;
    humanLikes?: number;
    agentLikes?: number;
    commentCount?: number;
    repostCount?: number;
    viewCount?: number;
    createdAt?: string;
    author?: {
      id: string;
      username: string;
      displayName?: string | null;
      avatarUrl?: string | null;
      accountType?: string;
    } | null;
    channel?: {
      id: string;
      name: string;
      slug: string;
    } | null;
    showBadge?: boolean;
    /**
     * When the post advertises a widget (content with type === 'widget'),
     * the workspace surfaces an "Add widget" action instead of the
     * default chat / add-agent CTA. See `WorkspaceCardActions.addWidget`
     * in `card-actions-context.tsx`. Optional everywhere else.
     */
    widgetContentId?: string | null;
    /**
     * Where the card goes. Defaults to `/p/<id>`, which is right for anything
     * that IS a post. Discover's Mine scope also lists the viewer's own
     * UNPUBLISHED apps (2026-09-06) and those have no post behind them, so
     * they point at `/content/<id>` — the owner's view of the app itself —
     * rather than at a 404.
     */
    href?: string | null;
    /**
     * Set on an owner-scoped app row with no marketplace listing behind it.
     * Badged so a person reading Mine can tell a private draft from something
     * that is live, the same distinction the Apps panel and the workspace
     * Discover shelf already draw.
     */
    appUnpublished?: boolean;
  };
  viewMode: ViewMode;
}

export function PostDiscoverCard({ post, viewMode }: PostDiscoverCardProps) {
  const { user } = useAuth();
  const [reportOpen, setReportOpen] = useState(false);
  const { t } = useTranslation();
  const chatSidebar = useChatSidebar();
  // See PostCard for the rationale: inside the workspace shell the
  // chat icon is repurposed (add-agent / add-widget). `null` on every
  // other surface keeps the legacy chat behaviour intact.
  const wsCardActions = useWorkspaceCardActions();
  const displayTitle = post.title || (post.content || '').slice(0, 100);
  const hasMedia = post.mediaType && (post.thumbnailUrl || post.mediaUrl);
  const isVideo = post.mediaType === 'video';
  const isAudio = post.mediaType === 'audio';
  const is3D = post.mediaType === '3d';
  const [starred, setStarred] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const isOwn = !!(user && post.author && post.author.id === user.id);
  const href = post.href || `/p/${post.id}`;
  // Reuses `catalogCard.unpublished` — the workspace Discover catalog card
  // already badges the maker's own unlisted apps with exactly this word, and
  // all 15 locales carry it.
  const unpublishedBadge = post.appUnpublished ? (
    <span className="shrink-0 rounded bg-foreground/10 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-muted-foreground">
      {t('catalogCard.unpublished')}
    </span>
  ) : null;

  const handleFollow = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || !post.author) { window.location.href = '/login'; return; }
    haptic('light');
    setFollowLoading(true);
    try {
      const res = await fetch(`/api/users/${post.author.username}/follow`, { method: 'POST' });
      if (res.ok) {
        setFollowing(prev => !prev);
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'user' } }));
      }
    } catch { /* ignore */ }
    setFollowLoading(false);
  }, [user, post.author]);

  const handleStar = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    const next = !starred;
    setStarred(next);
    try {
      await fetch('/api/users/me/starred', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemType: 'post', itemId: post.id }),
      });
      window.dispatchEvent(new Event('starChanged'));
    } catch {
      setStarred(!next);
    }
  }, [user, starred, post.id]);

  if (viewMode === 'list') {
    return (
      <Link href={href} className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-accent/50 transition-colors group">
        {hasMedia ? (
          <div className="relative h-10 w-10 rounded-lg bg-muted overflow-hidden shrink-0">
            {isAudio ? (
              post.thumbnailUrl ? (
                <OptimizedImage src={post.thumbnailUrl} alt="" fill className="object-cover" sizes="40px" />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-purple-900/80 to-indigo-900/80 flex items-center justify-center">
                  <Music className="h-4 w-4 text-white/60" />
                </div>
              )
            ) : is3D ? (
              post.thumbnailUrl ? (
                <OptimizedImage src={post.thumbnailUrl} alt="" fill className="object-cover" sizes="40px" />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-blue-900/80 to-cyan-900/80 flex items-center justify-center">
                  <Box className="h-4 w-4 text-white/60" />
                </div>
              )
            ) : (
              <OptimizedImage src={resolveCardImageSrc(post)} alt="" fill className="object-cover" sizes="40px" />
            )}
            {isVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Play className="h-3.5 w-3.5 text-white fill-white" />
              </div>
            )}
            {isAudio && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Play className="h-3.5 w-3.5 text-white fill-white" />
              </div>
            )}
            {is3D && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Box className="h-3.5 w-3.5 text-white" />
              </div>
            )}
          </div>
        ) : post.author ? (
          <Avatar seed={post.author.username} type={(post.author.accountType as 'agent' | 'human') || 'human'} src={post.author.avatarUrl} className="h-10 w-10 shrink-0" />
        ) : (
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium line-clamp-2">{displayTitle}</p>
            {isOwn && post.author && <Tip label={t('user.editAvatar')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/p/${post.id}`; }} className="cursor-pointer"><PenSquare className="h-3 w-3 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
          </div>
          <div className="flex items-center gap-1.5">
            {unpublishedBadge}
            {post.author && (
              <span className={cn(
                "text-xs font-medium truncate",
                post.author.accountType === 'agent' ? "text-purple-500" : "text-emerald-500"
              )}>
                @{post.author.username}
              </span>
            )}
            {post.author && (
              <span className={cn(
                "shrink-0 px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide leading-none",
                post.author.accountType === 'agent' ? "bg-purple-500/15 text-purple-500" : "bg-emerald-500/15 text-emerald-500"
              )}>
                {post.author.accountType === 'agent' ? t('user.agent') : t('user.human')}
              </span>
            )}
            {post.channel && (
              <span className="text-xs text-muted-foreground truncate">
                · //{post.channel.slug}
              </span>
            )}
          </div>
          {post.content && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{post.content.slice(0, 200)}</p>
          )}
          <div className="flex items-center gap-2.5 mt-1 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-0.5"><Heart className="h-3 w-3" />{formatCount(post.humanLikes || 0)}</span>
            <span className="flex items-center gap-0.5"><ChatIcon className="h-3 w-3" eyes={false} />{formatCount(post.commentCount || 0)}</span>
            {post.showBadge && (
              <span className="text-muted-foreground">· <Pin className="inline h-3 w-3 -mt-0.5" /> Pinned</span>
            )}
            {post.createdAt && (
              <span className="ml-auto">{formatRelativeTime(post.createdAt)}</span>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 ml-2">
          <CardMoreMenu onReport={user && !isOwn ? (e) => { e.preventDefault(); e.stopPropagation(); setReportOpen(true); } : undefined} starred={starred} onStar={handleStar} following={following} followLoading={followLoading} onFollow={post.author && !isOwn ? handleFollow : undefined} followLabel={{ follow: 'Follow Author', unfollow: 'Unfollow Author' }} />
          {/* Trailing CTA — see PostCard for the priority rules. */}
          {wsCardActions && post.widgetContentId ? (
            <Tip label={t('workspace.canvas.addWidgetCta')}>
              <span
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  wsCardActions.addWidget({
                    contentId: post.widgetContentId!,
                    title: post.title ?? null,
                    thumbnailUrl: post.thumbnailUrl ?? null,
                    metadata: (post as { widgetMetadata?: Record<string, unknown> | null }).widgetMetadata ?? null,
                    // An unpublished app has no release post, so `post.id` is
                    // its CONTENT id and there is nothing to attribute an
                    // install to. Passing it would stash an id that matches no
                    // post. Every other row is a real post and is unchanged.
                    sourceWidgetPostId: post.appUnpublished ? null : post.id,
                  });
                }}
                className="p-1.5 rounded-md text-primary hover:bg-primary/10 transition-colors cursor-pointer"
              >
                <Grid2x2Plus className="h-4.5 w-4.5" />
              </span>
            </Tip>
          ) : wsCardActions && post.author && post.author.accountType === 'agent' ? (
            <Tip label={t('workspace.canvas.addAgentCta')}>
              <span
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  wsCardActions.addAgent({
                    agentId: post.author!.id,
                    username: post.author!.username,
                    displayName: post.author!.displayName,
                    avatarUrl: post.author!.avatarUrl,
                  });
                }}
                className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
              >
                <ChatIcon className="h-4.5 w-4.5" eyes />
              </span>
            </Tip>
          ) : post.author && post.author.accountType === 'agent' ? (
            <Tip label={t('workspace.canvas.node.chat')}>
              <span
                role="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); chatSidebar.openDraftChat({ participantUsername: post.author!.username, prefillText: post.chatCta ? `${post.chatCta} ${window.location.origin}/p/${post.id}` : `${t('posts.learnMorePrompt')} ${window.location.origin}/p/${post.id}` }); }}
                className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
              >
                <ChatIcon className="h-4.5 w-4.5" eyes />
              </span>
            </Tip>
          ) : null}
        </div>
      </Link>
    );
  }

  const bodyExcerpt = post.content ? post.content.slice(0, 140) : '';

  return (
    <Link href={href} className="relative rounded-xl transition-all group min-w-0 border border-border bg-muted/40 hover:bg-muted/60 hover:border-border/80 h-full flex flex-col">
      <CardMoreMenu onReport={user && !isOwn ? (e) => { e.preventDefault(); e.stopPropagation(); setReportOpen(true); } : undefined} starred={starred} onStar={handleStar} following={following} followLoading={followLoading} onFollow={post.author && !isOwn ? handleFollow : undefined} followLabel={{ follow: 'Follow Author', unfollow: 'Unfollow Author' }} className="absolute top-1.5 right-1.5 z-20 [&_button]:text-white/80 [&_button]:hover:bg-white/20" />
      <div className="relative aspect-video bg-muted overflow-hidden rounded-t-xl">
        {hasMedia ? (
          <>
            {isAudio ? (
              post.thumbnailUrl ? (
                <OptimizedImage src={post.thumbnailUrl} alt="" fill className="object-cover" sizes="(max-width: 640px) 50vw, 400px" />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-purple-900/80 via-indigo-900/70 to-slate-900/90" />
              )
            ) : is3D ? (
              post.thumbnailUrl ? (
                <OptimizedImage src={post.thumbnailUrl} alt="" fill className="object-cover" sizes="(max-width: 640px) 50vw, 400px" />
              ) : post.mediaUrl ? (
                <div className="h-full w-full relative bg-gradient-to-br from-blue-950 via-slate-900 to-slate-950">
                  {/* @ts-expect-error model-viewer is a web component */}
                  <model-viewer
                    src={post.mediaUrl}
                    auto-rotate
                    interaction-prompt="none"
                    shadow-intensity="1"
                    environment-image="neutral"
                    exposure="1.2"
                    tone-mapping="agx"
                    loading="lazy"
                    style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, pointerEvents: 'none', background: 'transparent' }}
                  />
                </div>
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-blue-900/80 via-cyan-900/70 to-slate-900/90" />
              )
            ) : (
              <OptimizedImage
                src={resolveCardImageSrc(post)}
                alt=""
                fill
                className="object-cover"
                sizes="(max-width: 640px) 50vw, 400px"
              />
            )}
            {isAudio && (
              <div className="absolute inset-0 flex items-center justify-center pb-8">
                <div className="h-10 w-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
                  <Play className="h-5 w-5 text-white fill-white ml-0.5" />
                </div>
              </div>
            )}
            {/* Top-right media badges */}
            <div className="absolute top-1.5 right-10 z-10 flex items-center gap-1">
              {isVideo && (
                <div className="bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 flex items-center gap-1">
                  <Play className="h-3 w-3 text-white fill-white" />
                </div>
              )}
              {isAudio && (
                <div className="bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 flex items-center gap-1">
                  <Music className="h-3 w-3 text-white" />
                </div>
              )}
              {is3D && (
                <div className="bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 text-[10px] text-white font-medium">
                  3D
                </div>
              )}
            </div>
            {/* Author overlay top-left */}
            {post.author && (
              <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/60 via-black/25 to-transparent p-2.5 pb-6">
                <div className="flex items-center gap-1.5">
                  <Avatar seed={post.author.username} type={(post.author.accountType as 'agent' | 'human') || 'human'} src={post.author.avatarUrl} className="h-5 w-5 ring-1 ring-white/30" />
                  <span className="text-xs font-medium text-white drop-shadow-sm truncate">@{post.author.username}</span>
                </div>
              </div>
            )}
            {/* Title overlay bottom */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-8">
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium text-white line-clamp-2 flex-1 drop-shadow-sm">{displayTitle}</p>
                {isOwn && post.author && <Tip label={t('user.editAvatar')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/p/${post.id}`; }} className="cursor-pointer"><PenSquare className="h-3 w-3 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
              </div>
            </div>
          </>
        ) : (
          <div className="h-full bg-gradient-to-br from-primary/5 to-primary/10 flex flex-col p-3 pr-9">
            {post.author && (
              <div className="flex items-center gap-1.5 mb-2">
                <Avatar seed={post.author.username} type={(post.author.accountType as 'agent' | 'human') || 'human'} src={post.author.avatarUrl} className="h-5 w-5" />
                <span className={cn(
                  "text-xs font-medium truncate",
                  post.author.accountType === 'agent' ? "text-purple-500" : "text-emerald-500"
                )}>@{post.author.username}</span>
              </div>
            )}
            {displayTitle && <p className="text-sm font-medium line-clamp-2">{displayTitle}</p>}
            {bodyExcerpt && bodyExcerpt !== displayTitle && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{bodyExcerpt}</p>
            )}
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col flex-1">
        {(post.channel || unpublishedBadge) && (
          <div className="flex min-w-0 items-center gap-1.5 mb-1.5">
            {unpublishedBadge}
            {post.channel && (
              <p className="text-xs text-muted-foreground truncate">//{post.channel.slug}</p>
            )}
          </div>
        )}
        <div className="flex items-center gap-2.5 mt-auto pt-0.5 text-[11px] text-muted-foreground">
          <Tip label={t('discoverCard.humanLikes')}><span className="flex items-center gap-0.5"><Heart className="h-3 w-3" />{formatCount(post.humanLikes || 0)}</span></Tip>
          <Tip label={t('discoverCard.agentLikes')}><span className="flex items-center gap-0.5"><Cpu className="h-3 w-3" />{formatCount(post.agentLikes || 0)}</span></Tip>
          <span className="flex items-center gap-0.5"><ChatIcon className="h-3 w-3" eyes={false} />{formatCount(post.commentCount || 0)}</span>
          {post.showBadge && (
            <span className="text-muted-foreground">· <Pin className="inline h-3 w-3 -mt-0.5" /> Pinned</span>
          )}
          {/* Trailing CTA — see PostCard for the priority rules. */}
          {wsCardActions && post.widgetContentId ? (
            <Tip label={t('workspace.canvas.addWidgetCta')}>
              <span
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  wsCardActions.addWidget({
                    contentId: post.widgetContentId!,
                    title: post.title ?? null,
                    thumbnailUrl: post.thumbnailUrl ?? null,
                    metadata: (post as { widgetMetadata?: Record<string, unknown> | null }).widgetMetadata ?? null,
                    // An unpublished app has no release post, so `post.id` is
                    // its CONTENT id and there is nothing to attribute an
                    // install to. Passing it would stash an id that matches no
                    // post. Every other row is a real post and is unchanged.
                    sourceWidgetPostId: post.appUnpublished ? null : post.id,
                  });
                }}
                className="ml-auto p-1.5 rounded-md text-primary hover:bg-primary/10 transition-colors cursor-pointer"
              >
                <Grid2x2Plus className="h-4.5 w-4.5" />
              </span>
            </Tip>
          ) : wsCardActions && post.author && post.author.accountType === 'agent' ? (
            <Tip label={t('workspace.canvas.addAgentCta')}>
              <span
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  wsCardActions.addAgent({
                    agentId: post.author!.id,
                    username: post.author!.username,
                    displayName: post.author!.displayName,
                    avatarUrl: post.author!.avatarUrl,
                  });
                }}
                className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
              >
                <ChatIcon className="h-4.5 w-4.5" eyes />
              </span>
            </Tip>
          ) : post.author && post.author.accountType === 'agent' ? (
            <Tip label={t('workspace.canvas.node.chat')}>
              <span
                role="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); chatSidebar.openDraftChat({ participantUsername: post.author!.username, prefillText: post.chatCta ? `${post.chatCta} ${window.location.origin}/p/${post.id}` : `${t('posts.learnMorePrompt')} ${window.location.origin}/p/${post.id}` }); }}
                className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
              >
                <ChatIcon className="h-4.5 w-4.5" eyes />
              </span>
            </Tip>
          ) : null}
        </div>
      </div>
    <FlagReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="post" targetId={post.id} />
    </Link>
  );
}
